import clsx from "clsx"
import { useMemo, useRef, useState } from "react"

import useAppHotkey from "@hooks/useAppHotkey"
import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { HotKey, Icon } from "@atoms"

import { Menu } from "@organisms"
import type { MenuItemType } from "@organisms/Menu/Menu.types"

import { BILLS_ROUTES } from "@routes/bills"
import { CLIENTS_ROUTES } from "@routes/clients"
import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./SideBarCreateButton.module.scss"
import { SideBarCreateButtonProps } from "./SideBarCreateButton.types"

const OPEN_OVERFLOW_MENU_HOTKEY = "c"

export default function SideBarCreateButton(props: SideBarCreateButtonProps) {
    const { _isFocused = false, _isHovered = false, isPressed = false, isDisabled = false } = props

    const { user } = useUser()

    const { hasEntitlement, hasPermission } = useHasAccess()

    const triggerRef = useRef<HTMLButtonElement>(null)

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const toggleMenu = () => setIsOpen(!isOpen)

    useAppHotkey(
        OPEN_OVERFLOW_MENU_HOTKEY,
        () => {
            if (!isDisabled) {
                toggleMenu()
            }
        },
        {
            enabled: !isDisabled,
        },
        [isDisabled],
    )

    const [
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        creditNoteCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.CREATE,
        JOBS_ROUTES.CREATE,
        INVOICES_ROUTES.CREATE,
        CREDIT_NOTES_ROUTES.CREATE,
        PURCHASE_ORDER_ROUTES.CREATE,
        BILLS_ROUTES.CREATE,
        VENDOR_ROUTES.CREATE,
        CLIENTS_ROUTES.CREATE,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
    ])

    const menuContentItems = useMemo(() => {
        const items: MenuItemType[] = []

        if (
            hasEntitlement("entitlement_estimates_enabled") &&
            hasPermission("estimates_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.estimate.icon,
                path: estimateCreateRoute,
                label: objectMap.estimate.label,
            })
        }

        if (
            hasEntitlement("entitlement_jobs_enabled") &&
            hasPermission("jobs_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.job.icon,
                path: jobCreateRoute,
                label: objectMap.job.label,
            })
        }

        if (
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.invoice.icon,
                path: invoiceCreateRoute,
                label: objectMap.invoice.label,
            })
        }

        if (
            user?.service_company?.feature_credit_notes_enabled &&
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.credit_note.icon,
                path: creditNoteCreateRoute,
                label: objectMap.credit_note.label,
            })
        }

        if (
            hasEntitlement("entitlement_purchase_orders_enabled") &&
            hasPermission("purchase_orders_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.purchase_order.icon,
                path: purchaseOrderCreateRoute,
                label: objectMap.purchase_order.label,
            })
        }

        if (
            hasEntitlement("entitlement_bills_enabled") &&
            hasPermission("bills_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.bill.icon,
                path: billCreateRoute,
                label: objectMap.bill.label,
            })
        }

        if (hasPermission("vendors_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.vendor.icon,
                path: vendorCreateRoute,
                label: objectMap.vendor.label,
            })
        }

        if (hasPermission("clients_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.client.icon,
                path: clientCreateRoute,
                label: objectMap.client.label,
            })
        }

        if (hasPermission("pricebook_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                label: objectMap.pricebook_item.label,
                icon: objectMap.pricebook_item.icon,
                submenu: {
                    header: { title: "Create Pricebook Item", showCloseButton: true },
                    className: styles.pricebookSubMenu,
                    items: [
                        {
                            type: "group",
                            items: [
                                {
                                    label: "Service",
                                    path: pricebookItemServiceCreateRoute,
                                },
                                {
                                    label: "Part or Material",
                                    path: pricebookItemPartOrMaterialCreateRoute,
                                },
                                {
                                    label: "Other",
                                    path: pricebookItemMiscellaneousCreateRoute,
                                },
                                {
                                    label: "Discount",
                                    path: pricebookItemDiscountCreateRoute,
                                },
                                ...(isUsingTaxRates
                                    ? [
                                          {
                                              label: "Tax Rate",
                                              path: pricebookItemTaxRatesCreateRoute,
                                          } as const,
                                      ]
                                    : []),
                            ],
                        },
                    ],
                },
            })
        }

        return items
    }, [
        hasEntitlement,
        hasPermission,
        isUsingTaxRates,
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        creditNoteCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ])

    return (
        <Menu
            title="Create"
            className={styles.menu}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            trigger={
                <button
                    ref={triggerRef}
                    className={clsx(styles.base, {
                        [styles.disabled]: isDisabled,
                        [styles.focused]: _isFocused,
                        [styles.hovered]: _isHovered,
                        [styles.pressed]: isPressed || isOpen,
                    })}
                    tabIndex={isDisabled ? -1 : 0}
                    role="button"
                    onKeyDown={(e) => ifSpaceOrEnter(e, () => toggleMenu())}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                >
                    <div className={styles.iconWrapper}>
                        <Icon
                            className={styles.icon}
                            icon={faIcon({ name: "circle-plus", style: "solid" })}
                            size={10}
                        />
                    </div>
                    <div className={styles.label}>
                        <span>Create</span>
                    </div>
                    {!isDisabled && <HotKey symbolName="C" />}
                </button>
            }
            items={[{ type: "group", items: menuContentItems }]}
        />
    )
}
